import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { first, find, get, last } from 'lodash';

import CopyableValue from 'src/presentation/components/pages/email/components/CopyableValue';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import emailAddressesResource from 'src/actions/email/emailAddressesResource';
import emailDomainsResource from 'src/actions/email/emailDomainsResource';
import { AlertWarning } from '@ad-scripts/presentation/components/common/alert';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import {
  StepDescription,
  StepWizardActionContainer,
  StepWizardField,
} from 'src/presentation/components/pages/email/components/StepWizardLayout';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import { EmailAddress } from 'src/models/Email/EmailAddress';

interface AddEmailAddressStep2Type {
  firstStep?: () => void;
  onDone: () => void;
  value: EmailAddress;
  isWizardStep?: boolean;
}

const AddEmailAddressStep2 = ({ firstStep, onDone, value, isWizardStep }: AddEmailAddressStep2Type): JSX.Element => {
  const dispatch = useDispatch();
  const backHandler = () => {
    firstStep && firstStep();
  };
  const { values: emailDomains } = useResourceValues(emailDomainsResource);
  const emailUserName = first(get(value, 'address', '').split('@'));

  const emailDomain = useMemo(() => {
    if (!value) {
      return '';
    }

    const emailDomainString = last(get(value, 'address', '').split('@'));
    return find(emailDomains, value => value.content.customerDomain === emailDomainString);
  }, [emailDomains, value]);

  const copyableValue = `${emailUserName}@${get(emailDomain, 'content.inboundDomain', '')}`;
  const handleDone = () => {
    if (isWizardStep) {
      dispatch(emailAddressesResource.effects.createEmailAddress(value, { disableWindowBack: true }));
    }
    onDone();
  };

  return (
    <>
      <StepWizardField>
        {isWizardStep && <H3 data-aid="wizard-title">Add forwarding address</H3>}
        {get(emailDomain, 'content.withLegacyForwardingSetup', false) && (
          <StepDescriptionTop data-aid="wizard-description">
            You might have noticed that your forwarding email address looks different — we updated our forwarding email
            address format. No action is required at this time.
          </StepDescriptionTop>
        )}
        <AlertWarning>
          <b>Note:</b>&nbsp;Do not activate email forwarding until you are ready to launch Gladly for your
          company.&nbsp;
          <StyledExternalLink
            className="learnWhyHelpLink"
            data-aid="learn-why-help-link"
            label="Learn why"
            to="https://help.gladly.com/docs/add-email-address#forward-emails-to-gladly"
          />
        </AlertWarning>
        <StepDescription data-aid="wizard-description">
          In order for Gladly to receive emails sent to <b>{value?.senderName}</b> at <b>{value?.address}</b>, you must
          add the following forwarding address to your email service:
        </StepDescription>
        {/* @ts-ignore */}
        <CopyableValue value={copyableValue} />
      </StepWizardField>

      <StepWizardActionContainer>
        <Button onClick={handleDone} data-aid="step3-done-button">
          Done
        </Button>

        {isWizardStep && (
          <Button data-aid="editor-cancel" type={Button.Types.TEXT} onClick={backHandler}>
            Back
          </Button>
        )}
      </StepWizardActionContainer>
    </>
  );
};

export const StepDescriptionTop = styled(StepDescription)`
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.spacing.insetMedium};
`;

const StyledExternalLink = styled(ExternalLink)`
  text-decoration: underline;
  color: ${p => p.theme.colors.black};

  &:hover {
    text-decoration: underline;
  }
`;

export default AddEmailAddressStep2;
